/**
 * PhysicsTraceReporter (Wave 1.2, 2026-05-15) — drains the
 * PhysicsEventEmitter ring every 5s and pushes via Colyseus
 * `netobs:physicsTrace`. Server-side ring (PhysicsTraceBuffer)
 * caches per-session for /api/diag/physics-trace.
 *
 * Mirrors the BrowserPerfReporter pattern. Gated on isNetobsEnabled()
 * so prod sessions never emit physics traces.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import { physicsEventEmitter } from '../../diagnostics/PhysicsEventEmitter';
import type { PhysicsTraceSnapshotWire } from '../../../shared/diagnostics/PhysicsTraceTypes';

const REPORT_INTERVAL_MS = 5000;

const PhysicsTraceReporterInner: React.FC = () => {
  useEffect(() => {
    let lastFlushAt = Date.now();
    const timer = window.setInterval(() => {
      const now = Date.now();
      const windowMs = now - lastFlushAt;
      lastFlushAt = now;

      const { events, emitted, dropped } = physicsEventEmitter.drain();
      // Skip the push entirely if nothing happened — reduces wire chatter.
      if (events.length === 0 && dropped === 0) return;

      const snapshot: PhysicsTraceSnapshotWire = {
        capturedAt: now,
        windowMs,
        emittedCount: emitted,
        droppedCount: dropped,
        events,
      };

      try {
        const room = (globalThis as any).nm?.getRoom?.();
        if (room && typeof room.send === 'function') {
          room.send('netobs:physicsTrace', snapshot);
        }
      } catch {
        // diagnostic-only, never gameplay
      }
    }, REPORT_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, []);

  return null;
};

export const PhysicsTraceReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <PhysicsTraceReporterInner />;
};
